import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    CyberDepositIcon,
    CyberWithdrawIcon,
} from '@/components/ui/icons/wallet-icons';
import { toast } from '@/components/ui/toast';
import { SocialLoginButtons, useOAuthSession } from '@/features/auth';
import type { AnchorChainId } from '@/features/wallet/config/anchors';
import type {
    BridgeCatalog,
    BridgeQuoteResponse,
    SpeedJobResponse,
} from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import type { AnchorBalances, DepositSource } from '../services/directDeposit';
import type { CustodialDepositState, CustodialWithdrawState } from '../types';

import CustodialDepositForm, {
    DEPOSIT_PROCESSING_STATUSES,
} from './CustodialDepositForm';
import type { DirectDepositControls } from './CustodialDepositForm';
import CustodialOperationsPanel from './CustodialOperationsPanel';
import CustodialWithdrawForm from './CustodialWithdrawForm';
import type { DirectWithdrawControls } from './CustodialWithdrawForm';
import HyperCoreTopUpNotice from './HyperCoreTopUpNotice';

type TabKey = 'deposit' | 'withdraw';

interface CustodialPanelProps {
    open: boolean;
    onClose: () => void;
    defaultTab?: TabKey;
    prefillAmount?: number;
    custodialBalance: number | null;
    /** USDC the withdraw saga can actually move — the withdraw form's max. Server-provided. */
    withdrawableBalance?: number | null;
    /** Floor (USD) for the next withdrawal — drives the withdraw form's min hint; absent on older backends. */
    minWithdrawUsd?: number | null;
    /** Estimated flat cost (USD) of the next withdrawal — drives the net-receive hint; absent on older backends. */
    flatFeeUsd?: number | null;
    /** The user's stable balances across BOTH anchor chains (deposit sources). */
    anchorBalances: AnchorBalances;
    /** The wallet's native balance per anchor (CRO, ETH), for the deBridge fee check. */
    anchorNativeBalances?: Partial<Record<AnchorChainId, number | null>>;
    anchorChainId?: AnchorChainId;
    isBalanceLoading: boolean;
    /** The panel's single refresh: balance, operations and every deposit/withdraw source read. */
    onRefreshBalance: () => void;
    depositState: CustodialDepositState;
    onDepositConfirm: (amount: bigint, source: DepositSource) => Promise<void>;
    onDepositCancel: () => void;
    onDepositRetry: () => void;
    onDepositReset: () => void;
    onDepositContinue?: () => Promise<void>;
    canContinueDeposit?: boolean;
    /** Cancel a deposit still waiting on the wallet signature (nothing has moved yet). */
    onDepositCancelActive?: () => Promise<void>;
    canCancelDeposit?: boolean;
    withdrawState: CustodialWithdrawState;
    onWithdraw: (
        amount: bigint,
        destinationChainId: AnchorChainId,
        destinationCurrency?: string,
    ) => Promise<void>;
    onWithdrawAll: (
        destinationChainId: AnchorChainId,
        destinationCurrency?: string,
    ) => Promise<void>;
    onWithdrawReset: () => void;
    isWithdrawing: boolean;
    tokenDecimals: number;
    hasActivePosition?: boolean;
    /** A Snappy Wallet setup is running; it spends from this wallet, so hold deposits/withdrawals. */
    isReactivating?: boolean;
    /** The Snappy Wallet still needs activating; the bridged withdraw route says so instead of "no funds". */
    activationRequired?: boolean;
    /** EOA-direct HyperEVM withdrawal; the destination picker is hidden when absent. */
    directWithdraw?: DirectWithdrawControls;
    /** HyperEVM as a deposit source; the deposit "From" picker is hidden when absent. */
    directDeposit?: DirectDepositControls;
    /**
     * USDC sitting in the custodial trading wallet on HyperEVM instead of on HyperCore (display
     * units). Only HyperCore backs a fast open, so anything here is dead money until moved; the
     * balance pane says so and offers the one-button transfer.
     */
    hyperEvmHeldBalance?: number | null;
    onboardingRequired?: boolean;
    /**
     * The wallet-first cohort: signed in as an owner EOA with no social identity,
     * both custodial wallets already on chain, and only the first deposit left to
     * activate them. They get the deposit-first prompt, never the social sign-in
     * (which they cannot take) and never a "finish setting up" (there is nothing
     * left to set up).
     */
    walletReady?: boolean;
    /** Temporary launch gate: a voucher plus a queue turn is needed before a wallet exists. */
    admissionRequired?: boolean;
    /** Open the Snappy Wallet setup flow (closes this panel first). */
    onRequestSetup?: () => void;
    jobs?: SpeedJobResponse[];
    jobsLoading?: boolean;
    jobsFetching?: boolean;
    /**
     * Display pre-quotes for the amount being typed. Passed straight through to
     * the forms, which gate their own button on a successful quote for the
     * amount actually entered. Absent means no gate, exactly as before.
     */
    /** The backend's bridge registry, for the bridge testing switch; null while unknown. */
    bridgeCatalog?: BridgeCatalog | null;
    quoteWithdrawal?: (
        amount: bigint,
        destinationChainId: AnchorChainId,
        signal: AbortSignal,
        destinationCurrency?: string,
    ) => Promise<BridgeQuoteResponse>;
    quoteDeposit?: (
        amount: bigint,
        source: DepositSource | undefined,
        signal: AbortSignal,
    ) => Promise<BridgeQuoteResponse>;
}

const CustodialPanel: React.FC<CustodialPanelProps> = ({
    open,
    onClose,
    defaultTab = 'deposit',
    prefillAmount,
    custodialBalance,
    withdrawableBalance,
    minWithdrawUsd,
    flatFeeUsd,
    anchorBalances,
    anchorNativeBalances,
    anchorChainId,
    isBalanceLoading,
    onRefreshBalance,
    depositState,
    onDepositConfirm,
    onDepositCancel,
    onDepositRetry,
    onDepositReset,
    onDepositContinue,
    canContinueDeposit = false,
    onDepositCancelActive,
    canCancelDeposit = false,
    withdrawState,
    onWithdraw,
    onWithdrawAll,
    onWithdrawReset,
    isWithdrawing,
    tokenDecimals,
    hasActivePosition = false,
    isReactivating = false,
    activationRequired = false,
    directWithdraw,
    directDeposit,
    hyperEvmHeldBalance = null,
    onboardingRequired = false,
    walletReady = false,
    admissionRequired = false,
    onRequestSetup,
    bridgeCatalog = null,
    quoteWithdrawal,
    quoteDeposit,
    jobs = [],
    jobsLoading = false,
    jobsFetching = false,
}) => {
    const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);
    const [operationsOpen, setOperationsOpen] = useState(false);
    const { isAuthenticated: isSocialAuthed, displayName } = useOAuthSession();

    useEffect(() => {
        if (open) setActiveTab(defaultTab);
    }, [open, defaultTab]);

    // both tabs sign with the SAME wallet, so an operation on one side locks the other out
    // rather than racing it into a nonce conflict / half-switched chain.
    const depositRunning =
        DEPOSIT_PROCESSING_STATUSES.has(depositState.status) ||
        !!directDeposit?.isRunning;
    const withdrawRunning = isWithdrawing || !!directWithdraw?.isRunning;

    const handleClose = () => {
        onDepositCancel();
        onWithdrawReset();
        onClose();
    };

    return (
        <DialogPrimitive.Root
            open={open}
            onOpenChange={(v) => !v && handleClose()}
        >
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                <DialogPrimitive.Content
                    // the trading-window banner floats above this panel; pressing its
                    // Reauthenticate must not count as clicking outside and close the panel
                    onInteractOutside={(event) => {
                        if (
                            event.target instanceof Element &&
                            event.target.closest(
                                '[data-trading-session-warning]',
                            )
                        ) {
                            event.preventDefault();
                        }
                    }}
                    className={cn(
                        'fixed z-50 flex flex-col outline-none',
                        'border border-border bg-card',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=open]:duration-300 data-[state=open]:ease-out',
                        'data-[state=closed]:duration-200 data-[state=closed]:ease-in',
                        'inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl',
                        'max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:slide-in-from-bottom',
                        'md:inset-y-2 md:bottom-2 md:left-auto md:right-2 md:w-[400px] md:max-h-none md:rounded-xl',
                        'md:data-[state=closed]:slide-out-to-right-[calc(100%+0.5rem)] md:data-[state=open]:slide-in-from-right-[calc(100%+0.5rem)]',
                    )}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-border px-4 py-3">
                        <DialogPrimitive.Title className="text-sm font-semibold text-foreground">
                            Quick trade wallet
                        </DialogPrimitive.Title>
                        <DialogPrimitive.Close asChild>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Close"
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <X size={14} />
                            </Button>
                        </DialogPrimitive.Close>
                    </div>

                    {/* Balance Display */}
                    <div className="flex items-center justify-between border-b border-border px-4 py-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                Available
                            </p>
                            <p
                                className={cn(
                                    'font-mono text-xl font-bold tracking-tight tabular-nums',
                                    custodialBalance === 0
                                        ? 'text-muted-foreground'
                                        : 'text-foreground',
                                )}
                            >
                                $
                                {isBalanceLoading
                                    ? '—'
                                    : (custodialBalance?.toFixed(2) ?? '0.00')}
                            </p>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={onRefreshBalance}
                            disabled={isBalanceLoading || jobsFetching}
                            className="text-muted-foreground hover:text-foreground"
                            title="Refresh wallet"
                        >
                            <RefreshCw
                                size={12}
                                className={cn(
                                    (isBalanceLoading || jobsFetching) &&
                                        'animate-spin',
                                )}
                            />
                        </Button>
                    </div>
                    <HyperCoreTopUpNotice
                        heldUsd={hyperEvmHeldBalance}
                        depositState={depositState}
                        onTransfer={onDepositConfirm}
                        onDone={onDepositReset}
                    />

                    {/* Tab Switcher */}
                    <div className="grid grid-cols-2 border-b border-border">
                        <button
                            onClick={() => setActiveTab('deposit')}
                            aria-pressed={activeTab === 'deposit'}
                            className={cn(
                                'group flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors',
                                activeTab === 'deposit'
                                    ? 'border-b-2 border-primary text-foreground'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            <CyberDepositIcon size={18} />
                            Deposit
                        </button>
                        <button
                            onClick={() => setActiveTab('withdraw')}
                            aria-pressed={activeTab === 'withdraw'}
                            className={cn(
                                'group flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors',
                                activeTab === 'withdraw'
                                    ? 'border-b-2 border-primary text-foreground'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            <CyberWithdrawIcon size={18} />
                            Withdraw
                        </button>
                    </div>

                    {onboardingRequired && !depositRunning && (
                        <div className="border-b border-amber-500/20 bg-amber-500/5 px-4 py-3">
                            <div className="flex gap-3">
                                <AlertTriangle
                                    size={18}
                                    className="mt-0.5 shrink-0 text-amber-400"
                                />
                                <div className="min-w-0 flex-1">
                                    {walletReady ? (
                                        // the wallet cohort: both custodial wallets exist on chain and
                                        // the launch gate does not apply (it guards wallet creation).
                                        // No social login to name, no setup to resume.
                                        <>
                                            <p className="text-[11px] font-semibold text-amber-300">
                                                One deposit away
                                            </p>
                                            <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                                                Your Snappy Wallet is ready.
                                                Make your first deposit to
                                                activate trading.
                                            </p>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    setActiveTab('deposit')
                                                }
                                                className="mt-3 w-full gap-1 border-amber-500/30 text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10"
                                            >
                                                Deposit to activate
                                            </Button>
                                        </>
                                    ) : isSocialAuthed ? (
                                        <>
                                            <p className="text-[11px] font-semibold text-amber-300">
                                                {admissionRequired
                                                    ? 'Launch access required'
                                                    : 'One deposit away'}
                                            </p>
                                            <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                                                {`Signed in${
                                                    displayName
                                                        ? ` as ${displayName}`
                                                        : ''
                                                }. ${
                                                    admissionRequired
                                                        ? 'Snappy Wallet setup is limited during launch. Enter your launch code to join the queue.'
                                                        : 'Your Snappy Wallet is created automatically with your first deposit.'
                                                }`}
                                            </p>
                                            {/* no Refresh button: the deposit saga already re-polls the
                                                balance via refetchUntilSettled once it completes. */}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    if (
                                                        admissionRequired &&
                                                        onRequestSetup
                                                    ) {
                                                        handleClose();
                                                        onRequestSetup();
                                                        return;
                                                    }
                                                    setActiveTab('deposit');
                                                }}
                                                className="mt-3 w-full gap-1 border-amber-500/30 text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10"
                                            >
                                                {admissionRequired
                                                    ? 'Get launch access'
                                                    : 'Deposit to activate'}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-[11px] font-semibold text-amber-300">
                                                Finish setting up your Snappy
                                                Wallet
                                            </p>
                                            <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                                                Connect your Cronos wallet to
                                                create your gasless wallet. You
                                                can add a social login
                                                afterwards.
                                            </p>
                                            {onRequestSetup ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        handleClose();
                                                        onRequestSetup();
                                                    }}
                                                    className="mt-3 w-full gap-1 border-amber-500/30 text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10"
                                                >
                                                    Continue setup
                                                </Button>
                                            ) : (
                                                <SocialLoginButtons
                                                    className="mt-3"
                                                    onError={(message) =>
                                                        toast.error(message)
                                                    }
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <CustodialOperationsPanel
                        jobs={jobs}
                        isLoading={jobsLoading}
                        expanded={operationsOpen}
                        onExpandedChange={setOperationsOpen}
                    />

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4">
                        {activeTab === 'deposit' ? (
                            <CustodialDepositForm
                                prefillAmount={prefillAmount}
                                depositState={depositState}
                                onConfirm={onDepositConfirm}
                                onCancel={handleClose}
                                onRetry={onDepositRetry}
                                onReset={onDepositReset}
                                onContinue={onDepositContinue}
                                canContinue={canContinueDeposit}
                                onCancelActive={onDepositCancelActive}
                                canCancelActive={canCancelDeposit}
                                anchorBalances={anchorBalances}
                                anchorNativeBalances={anchorNativeBalances}
                                anchorChainId={anchorChainId}
                                tokenDecimals={tokenDecimals}
                                isReactivating={isReactivating}
                                otherOperationRunning={withdrawRunning}
                                directDeposit={directDeposit}
                                quoteDeposit={quoteDeposit}
                                bridgeCatalog={bridgeCatalog}
                                onRequestSetup={
                                    onRequestSetup &&
                                    (() => {
                                        handleClose();
                                        onRequestSetup();
                                    })
                                }
                            />
                        ) : (
                            <CustodialWithdrawForm
                                withdrawState={withdrawState}
                                onWithdraw={onWithdraw}
                                onWithdrawAll={onWithdrawAll}
                                onReset={onWithdrawReset}
                                onCancel={handleClose}
                                // the SERVER's withdrawable figure, never a locally
                                // recomposed total: the total counts HyperCore dust below
                                // the withdraw floor and the exit-sweep gas buffer, and the
                                // withdraw pipeline refuses both.
                                custodialBalance={withdrawableBalance ?? null}
                                minWithdrawUsd={minWithdrawUsd}
                                flatFeeUsd={flatFeeUsd}
                                isWithdrawing={isWithdrawing}
                                tokenDecimals={tokenDecimals}
                                hasActivePosition={hasActivePosition}
                                isReactivating={isReactivating}
                                activationRequired={activationRequired}
                                totalBalance={custodialBalance}
                                directWithdraw={directWithdraw}
                                otherOperationRunning={depositRunning}
                                anchorChainId={anchorChainId}
                                quoteWithdrawal={quoteWithdrawal}
                                bridgeCatalog={bridgeCatalog}
                            />
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-border px-4 py-2">
                        <p className="text-center text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Quick trade wallet · Speed trading
                        </p>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};

export default CustodialPanel;
